import * as React from 'react';

import ErrorBoundary from '@components/ErrorBoundary';
import { PRICE_LABEL_CURRENCY_TEST_ID } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/variantsStyled';
import { IPrice } from '@containers/ProductListing/model';

import { Wrapper, Currency, Span, LineHeight } from './components';
import { ICurrencyProps } from './components/Currency';
import { PD_PRICE_WRAPPER_TEST_ID } from './components/styled';
import { useFormattedPrice } from './useFormattedPrice';

interface IPriceLabel {
  price: IPrice;
  lineHeight?: LineHeight;
  currencyComponent?: React.ComponentType<ICurrencyProps>;
  wrapperComponent?: React.ComponentType<Record<string, unknown>>;
  id?: string;
  currencySymbolOverride?: string;
  dataTestId?: string;
}

const PriceLabel: React.FC<IPriceLabel> = ({
  price,
  lineHeight,
  currencyComponent: CurrencyComponent = Currency,
  wrapperComponent: WrapperComponent = Wrapper,
  id,
  currencySymbolOverride,
  dataTestId,
}) => {
  const formattedPriceObj = useFormattedPrice(price);

  if (!formattedPriceObj) {
    return null;
  }

  const {
    formattedPrice,
    currentLocale: { priceFormat },
    currencySymbol,
    currentCurrency,
  } = formattedPriceObj;

  // format is specified for ex like 'v s' where 'v' is value, 's' is symbol
  const isPrefix = priceFormat?.startsWith('s') ?? false;
  const isSpaced = (priceFormat?.charAt(1) ?? '') === ' ';

  const symbol = (
    <CurrencyComponent
      content={currentCurrency.code ?? ''}
      isSpaced={isSpaced}
      isPrefix={isPrefix}
      data-testid={PRICE_LABEL_CURRENCY_TEST_ID}
    >
      {currencySymbolOverride || currencySymbol}
    </CurrencyComponent>
  );

  return (
    <ErrorBoundary>
      <WrapperComponent
        lineHeight={lineHeight}
        id={id}
        data-testid={PD_PRICE_WRAPPER_TEST_ID}
      >
        {isPrefix && symbol}
        <Span data-testid={dataTestId} content={formattedPrice}>
          {formattedPrice}
        </Span>
        {!isPrefix && symbol}
      </WrapperComponent>
    </ErrorBoundary>
  );
};

export default PriceLabel;
